import { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';
import routesfe from '../Config/RoutesFE';
import Toast from '../components copy/Toast';

function SignUpVerifyEmail({ email, setStage }) {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const inputRefs = useRef([]);

  const navigate = useNavigate();
  const { handleSubmit } = useForm();

  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Handle input change for individual digit boxes
  const handleChange = (index, value) => {
    // Only allow digits
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace to move to previous input
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleConfirmSignup = async (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    setLoading(true);
    try {
      await Auth.confirmSignUp(email, verificationCode);
      setToast({ message: 'Signup confirmed! You can now log in. 🎉', type: 'success' });
      setIsRedirecting(true);
      setTimeout(() => {
        navigate(routesfe.LOGIN);
      }, 1500);
    } catch (error) {
      setError(error.message || 'Invalid verification code');
      setToast({ message: error.message || 'Invalid verification code', type: 'error' });
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      await Auth.resendSignUp(email);
      setToast({ message: 'A new verification code has been sent to your email.', type: 'success' });
      setResendCooldown(30); // 30 seconds cooldown
    } catch (error) {
      setToast({ message: error.message || 'Failed to resend code.', type: 'error' });
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <>
      <Toast toast={toast} onClose={() => setToast(null)} />
      {isRedirecting && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl p-6 shadow-2xl flex flex-col items-center gap-3 max-w-xs mx-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-indigo-600"></div>
            <p className="text-sm font-semibold text-gray-700">Email Verified! Redirecting to login...</p>
          </div>
        </div>
      )}
      <form onSubmit={handleConfirmSignup}>
      <div className='mt-4 grid grid-cols-1 gap-3'>
        <div>
          <label className='block text-sm sm:text-base font-medium text-gray-700 mb-4'>
            A verification code has been sent to <strong>{email}</strong>. Please enter the code below to complete your registration.
          </label>
          
          {/* Verification Code Input Boxes */}
          <div className='flex justify-center gap-2 sm:gap-3'>
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type='text'
                inputMode='numeric'
                maxLength='1'
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className={`w-12 h-12 sm:w-14 sm:h-14 text-center text-2xl font-bold border-2 rounded-lg transition-all duration-200 focus:outline-none ${
                  error
                    ? 'border-red-400 bg-red-50 focus:border-red-500 focus:ring-2 focus:ring-red-300'
                    : 'border-gray-300 bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-300'
                }`}
                placeholder='-'
              />
            ))}
          </div>

          {error && (
            <p className='text-red-600 text-sm font-medium mt-3 text-center'>{error}</p>
          )}
        </div>
        
        <div className='flex justify-center'>
          <button
            type='submit'
            disabled={loading}
            className={`px-8 py-2 text-white font-semibold rounded-md transition ${
              loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-500'
            }`}
          >
            {loading ? 'Verifying...' : 'Confirm'}
          </button>
        </div>

        <div className='text-center'>
          <button
            type='button'
            onClick={handleResendCode}
            disabled={resendLoading || resendCooldown > 0}
            className={`text-indigo-600 font-medium transition ${
              resendLoading || resendCooldown > 0 ? 'opacity-50 cursor-not-allowed' : 'hover:underline'
            }`}
          >
            {resendCooldown > 0 ? `Resend Code (${resendCooldown}s)` : 'Resend Code'}
          </button>
        </div>
      </div>
      </form>
    </>
  );
}

export default SignUpVerifyEmail;
